import React, { useMemo, useState, useRef } from "react";
import {
  fetchParticipant,
  updateParticipant,
  createParticipant,
  addAttachmentParticipant,
  deleteAttachmentParticipant,
} from "api/actions/agenda";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import DragZone from "components/Shared/DragZone";
import { fetcher } from "api/client";
import { useForm, Controller } from "react-hook-form";
import { uploadFileV2 } from "api/actions/file";
import { toast } from "components/Shared/Toast";
import { fileTypes } from "../ContractList/CreateContract/Constants/types";
import DatePicker from "components/Shared/DatePicker";
import clsx from "clsx";
import bootbox from "components/Modals/Bootbox";
import {
  Modal,
  ModalHeader,
  ModalBody,
  InputGroup,
  InputGroupAddon,
  Input,
  UncontrolledTooltip,
  InputGroupText,
  Collapse,
  FormFeedback,
  FormGroup,
  Button,
  Label,
  Spinner,
} from "reactstrap";
import { emailRegex, parseDate } from "utils";

function Documents({
  handleFile,
  askFileName,
  editMode,
  participant,
  handleDeleteAttachment,
  isFileLoading,
}) {
  return (
    <>
      <div className="mb-2">
        {participant?.documents?.map((document) => (
          <div key={document._id} className="input-group input-group-md mb-2">
            <InputGroupAddon
              id={document._id}
              name={"propertyName"}
              addonType="prepend"
            >
              <InputGroupText>
                {" "}
                <i className="fas fa-file" />
              </InputGroupText>
            </InputGroupAddon>
            <span className="form-control">
              <a href={document.url} target="_blank" rel="noopener noreferrer">
                {document.name}
              </a>
              <i></i>
            </span>
            <InputGroupAddon
              addonType="append pointer"
              className={clsx(editMode ? "" : "d-none")}
              onClick={() => handleDeleteAttachment(document._id)}
            >
              <InputGroupText>
                <i
                  className={clsx(
                    editMode
                      ? "fas fa-trash pull-right text-danger pointer"
                      : "d-none"
                  )}
                />
              </InputGroupText>
            </InputGroupAddon>
          </div>
        ))}

        {isFileLoading && (
          <div key={document._id} className="input-group input-group-md mb-2">
            <InputGroupAddon
              id={document._id}
              name={"propertyName"}
              addonType="prepend"
            >
              <InputGroupText>
                {" "}
                <i className="fas fa-file" />
              </InputGroupText>
            </InputGroupAddon>
            <span className="form-control">
              <a href={document.url} target="_blank" rel="noopener noreferrer">
                {document.name}
              </a>
              <i></i>
            </span>
            <InputGroupAddon
              addonType="append"
              className={clsx(editMode ? "" : "d-none")}
            >
              <InputGroupText>
                <Spinner
                  type="border"
                  role="status"
                  style={{
                    borderWidth: "1.5px",
                    width: "1rem",
                    height: "1rem",
                  }}
                />
              </InputGroupText>
            </InputGroupAddon>
          </div>
        )}
      </div>

      {editMode && participant?._id && (
        <DragZone
          imgSrc="/images/listing/createDocument/subir.svg"
          className=""
          multiple={true}
          acceptedFiles={fileTypes}
          handleFile={handleFile}
          askFileName={askFileName}
        />
      )}
    </>
  );
}

function FormButtons({
  editMode,
  register,
  participant,
  errors,
  formButtons,
  expand,
  control,
}) {
  const { t, i18n } = useTranslation();

  let isMoral = () => {
    if (
      participant?.representanteLegal?.length > 0 &&
      participant?.razonSocial?.length > 0
    )
      return true;
  };

  return formButtons.map(({ propertyName, required, icon, type }) => {
    if (propertyName === "razonSocial" && isMoral()) {
      icon = "building";
    }

    const value = participant[propertyName];

    return (
      <Collapse key={propertyName} isOpen={expand}>
        <FormGroup>
          <InputGroup>
            {/*className={propertyName === 'razonSocial' ? 'd-none' : ''}*/}
            <UncontrolledTooltip target={propertyName}>
              {t(`agenda.${propertyName}_placeholder`)}
            </UncontrolledTooltip>
            <InputGroupAddon
              id={propertyName}
              name={propertyName}
              addonType="prepend"
            >
              <InputGroupText>
                {" "}
                <i className={clsx(`fas fa-${icon}`)} />
              </InputGroupText>
            </InputGroupAddon>

            {type === "calendar" ? (
              <Controller
                render={({ onChange, value: newValue }) =>
                  editMode && (
                    <div style={{ width: "calc(100% - 37px)" }}>
                      <DatePicker
                        locale={i18n.language}
                        selected={newValue ? new Date(newValue) : null}
                        onChange={(date) => {
                          onChange(date);
                        }}
                        className={clsx("form-control")}
                        customInput={
                          <input type="text" className="form-control" />
                        }
                        dateFormat="dd/MMM/yyyy"
                        placeholderText={t(
                          `agenda.${propertyName}_placeholder`
                        )}
                      />
                    </div>
                  )
                }
                id={propertyName}
                name={propertyName}
                control={control}
                defaultValue={value}
              />
            ) : (
              <Input
                id={propertyName}
                name={propertyName}
                defaultValue={value}
                innerRef={register({
                  required,
                  pattern: type === "email" ? emailRegex : /./,
                  maxLength: 100,
                })}
                invalid={errors[propertyName]}
                hidden={!editMode}
                placeholder={t(`agenda.${propertyName}_placeholder`)}
              />
            )}
            {!editMode && (
              <Label className={clsx("form-control")}>
                {type === "calendar" ? parseDate(value, "PP") : value}
              </Label>
            )}
          </InputGroup>

          <FormFeedback invalid={errors[propertyName]}>
            {t("errors.Missingfields")}
          </FormFeedback>
          {/* {editMode ? t(`agenda.${propertyName}_placeholder`) : type === 'calendar' ? parseDate(value, 'PP') : value} */}
        </FormGroup>
      </Collapse>
    );
  });
}

export const formButtons = [
  { propertyName: "name", required: true, icon: "user" },
  { propertyName: "last_name", icon: "user" },
  { propertyName: "rfc", icon: "database" },
  { propertyName: "razonSocial", icon: "database" },
  { propertyName: "representanteLegal", icon: "database" },
  { propertyName: "email", icon: "envelope", type: "email" },
  { propertyName: "phone", icon: "phone" },
  { propertyName: "zipcode", icon: "map-marker" },
];

export const extraFormButtons = [
  { propertyName: "street", icon: "map-marker" },
  { propertyName: "suburb", icon: "map-marker" },
  { propertyName: "city", icon: "map-marker" },
  { propertyName: "state", icon: "map-marker" },
  { propertyName: "type", icon: "database" },
  { propertyName: "curp", icon: "database" },
  { propertyName: "nationality", icon: "database" },
  { propertyName: "escrituraPublica", icon: "database" },
  { propertyName: "notario", icon: "database" },
  { propertyName: "notariaNumero", icon: "database" },
  { propertyName: "ciudadNotaria", icon: "database" },
  { propertyName: "ciudadDeRegistroPublicoDeComercio", icon: "database" },
  { propertyName: "folioMercantil", icon: "database" },
  { propertyName: "poderEscrituraPublica", icon: "database" },
  { propertyName: "poderNotario", icon: "database" },
  { propertyName: "poderNotariaNumero", icon: "database" },
  { propertyName: "poderCiudadNotaria", icon: "database" },
  { propertyName: "poderFolioMercantil", icon: "database" },
  {
    propertyName: "fechaDeLaEscrituraPublica",
    icon: "clock",
    type: "calendar",
  },
  { propertyName: "fechaRegistroPublico", icon: "clock", type: "calendar" },
  { propertyName: "poderFechaDeEscritura", icon: "clock", type: "calendar" },
  { propertyName: "poderFechaFolioMercantil", icon: "clock", type: "calendar" },
];

export const agendaFields = [formButtons.filter(f => ['rfc', 'zipcode'].includes(f.propertyName)), ...extraFormButtons].flat()

function AgendaModal({ toggle, modalInfo, setmodalInfo }) {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { participantId, isOpen, editMode } = modalInfo;

  const [expand, setexpand] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const { register, handleSubmit, errors, control, reset, getValues } = useForm({
    mode: "all",
  });

  const { data: participant = {} } = useQuery(
    ["participant-", participantId],
    () => fetcher(fetchParticipant({ participantId })),
    {
      enabled: !!participantId,
      onSuccess: () => {
        // reset();
      },
    }
  );

  const { mutate: updateParticipantMutation, isLoading } = useMutation(
    (payload) => fetcher(updateParticipant(participantId, payload)),
    {
      onSuccess: async (data) => {
        queryClient.setQueryData(["participant-", participantId], data);
        queryClient.invalidateQueries(["participants"]);
        toast.success(t("agenda.success"));
        setmodalInfo({ ...modalInfo, editMode: false });
      },
    }
  );

  const { mutate: createParticipantMutation } = useMutation(
    (payload) => fetcher(createParticipant(payload)),
    {
      onSuccess: (newParticipant) => {
        toast.success(t("success"));
        queryClient.invalidateQueries("participants");
        setmodalInfo({
          ...modalInfo,
          editMode: false,
          participantId: newParticipant._id,
        });
      },
    }
  );

  const { mutate: addAttachmentParticipantMutation } = useMutation(
    (payload) => fetcher(addAttachmentParticipant(participantId, payload)),
    {
      onSuccess: (data) => {
        console.log({ data });
        toast.success(t("success"));
        queryClient.setQueryData(["participant-", participantId], data);
        setIsFileLoading(false);
      },
    }
  );

  const { mutate: deleteAttachmentParticipantMutation } = useMutation(
    (attachmentId) =>
      fetcher(deleteAttachmentParticipant(participantId, attachmentId)),
    {
      onSuccess: (data) => {
        toast.success(t("success"));
        queryClient.invalidateQueries(["participant-", participantId]);
      },
    }
  );

  const handleFile = async (fileArr) => {
    fileArr.forEach(async (file) => {
      const newName = await askFileName(file);
      const fileSplit = file.path.split(".");
      const ext = fileSplit[fileSplit.length - 1];
      const newFileName = `${newName}.${ext}`;
      const uploadResult = await fetcher(uploadFileV2(file));
      uploadResult.BaseFileName = newFileName;
      setIsFileLoading(true);
      addAttachmentParticipantMutation(uploadResult);
    });
  };

  const askFileName = (file) => {
    return new Promise(async (resolve, reject) => {
      // Removes extension from file name
      const fileName = file.name.split(".");
      fileName.pop();

      const name = await bootbox.prompt({
        text: t("agenda.ask_filename", { file_name: file.name }),
        inputText: fileName,
      });
      if (!name || name === "")
        return toast.error(t("agenda.document_name_missing"));
      resolve(name);
    });
  };

  const handleDeleteAttachment = async (attachmentId) => {
    const res = await bootbox.confirm(t("agenda.confirm_delete"));
    if (res) deleteAttachmentParticipantMutation(attachmentId);
  };

  const onSubmit = (payload) => {
    participantId
      ? updateParticipantMutation(payload)
      : createParticipantMutation(payload);
  };

  const handleEditMode = () => {
    setmodalInfo({ ...modalInfo, editMode: true });
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} centered size="lg">
      <ModalHeader toggle={toggle}>
        {participant.name || t("agenda.create")}
      </ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <div>
              <span className="">{t("agenda.details")}</span>{" "}
              <Label className="float-right" hidden={editMode}>
                <i
                  className="fas fa-edit text-info pointer"
                  onClick={handleEditMode}
                ></i>
              </Label>
            </div>
            <FormButtons
              expand={true}
              participant={participant}
              editMode={editMode}
              register={register}
              errors={errors}
              control={control}
              formButtons={formButtons}
            />
            <hr className="sidebar-divider" />

            <div className="agenda-details mt-3">
              {t("agenda.extra-details")}{" "}
              <i
                className={clsx(
                  "fas",
                  expand ? "fa-chevron-up" : "fa-chevron-down",
                  "pull-right pointer"
                )}
                onClick={() => setexpand(!expand)}
              ></i>
            </div>
            <FormButtons
              expand={expand}
              participant={participant}
              editMode={editMode}
              register={register}
              errors={errors}
              control={control}
              formButtons={extraFormButtons}
            />
            <hr className="sidebar-divider" />

            <div className="agenda-details mt-3">{t("agenda.documents")}</div>
            <Documents
              participant={participant}
              editMode={editMode}
              isFileLoading={isFileLoading}
              handleFile={handleFile}
              handleDeleteAttachment={handleDeleteAttachment}
              askFileName={askFileName}
            />

            <Button
              block
              color="light"
              hidden={!editMode}
              type="submit"
              className="mt-2"
              disabled={isLoading}
            >
              <i
                className={clsx(
                  "fas fa-circle-notch fa-spin",
                  isLoading || "d-none"
                )}
              ></i>{" "}
              {t("agenda.save")}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
}

export default AgendaModal;
